import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Grid, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { Container } from './atomic';
import FooterSubscribe from './FooterSubscribe';
import SecureEmail from './SecureEmail';
import { Logo } from './ui';

/**
 * Super Footer component with logo, navigation, and company information
 *
 * @returns {JSX.Element} Footer component
 */
function Footer() {
  const navigate = useNavigate();
  const { blogInsightsEnabled, letsCollaborateEnabled, joinPagesEnabled } =
    useFeatureFlags();
  const { trackEvent, trackCTA } = useAnalytics();

  const quickLinksCol1 = [
    { text: 'Home', href: '/' },
    { text: 'Our Community', href: '/#collective' },
    { text: 'Research & Stories', href: '/updates' },
    { text: 'RSMC Services', href: '/#services' },
    // Conditionally add Let's Collaborate
    ...(letsCollaborateEnabled
      ? [{ text: "Let's Collaborate", href: '/lets-collaborate' }]
      : []),
  ];

  const quickLinksCol2 = [
    // Conditionally add join pages (separate from main Let's Collaborate)
    ...(joinPagesEnabled
      ? [
          { text: 'Join as Consultant', href: '/join-consultant' },
          { text: 'Join as Integrator', href: '/join-integrator' },
          { text: 'Collaborate With Us', href: '/collaborate' },
        ]
      : []),
    // Conditionally add Insights/Blog
    ...(blogInsightsEnabled ? [{ text: 'Insights', href: '/blog' }] : []),
  ];

  const legalLinks = [
    { text: 'Privacy Policy', href: '/privacy' },
    { text: 'Terms of Use', href: '/terms' },
    { text: 'Cookie Policy', href: '/cookies' },
    { text: 'Accessibility', href: '/accessibility' },
    { text: 'California Privacy', href: '/california-privacy' },
  ];

  const handleLogoClick = () => {
    trackEvent('logo_click', {
      location: 'footer',
      action: 'navigate_home',
    });
    navigate('/');
    // Let ScrollToTop component handle the scrolling
  };

  const handleLinkClick = (href, linkText = '') => {
    trackCTA('footer_nav', linkText || href, {
      destination: href,
      section: 'quick_links',
    });
    navigate(href);
    // Let ScrollToTop component handle the scrolling
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'var(--color-secondary)',
        color: 'var(--color-secondary-text)',
        mt: 0,
        borderTop: '4px solid var(--color-primary)',
      }}
    >
      {/* Large Tagline Section */}
      <Box
        sx={{
          backgroundColor: 'var(--color-secondary)',
          py: 4,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Container maxWidth="lg"></Container>
      </Box>

      {/* Main Footer Content */}
      <Container maxWidth="lg">
        <Box sx={{ py: 6 }}>
          <Grid container spacing={8} sx={{ justifyContent: 'space-between' }}>
            {/* Company Info Section - Left */}
            <Grid item xs={12} md={4}>
              <Box>
                <Logo
                  onClick={handleLogoClick}
                  sx={{
                    height: '48px',
                    mb: 3,
                    opacity: 0.95,
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    maxWidth: '320px',
                  }}
                >
                  Modernizing the employee, patient, civic and client
                  experience.
                </Typography>

                <FooterSubscribe />
              </Box>
            </Grid>

            {/* Quick Links Section - Both Columns */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--color-primary)',
                }}
              >
                Quick Links
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: { xs: 1.2, sm: 2 },
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}
                >
                  {quickLinksCol1.map((link, index) => (
                    <Link
                      key={index}
                      to={link.href}
                      onClick={() => handleLinkClick(link.href, link.text)}
                      style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.color = 'var(--color-primary)')
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.color = 'rgba(255, 255, 255, 0.7)')
                      }
                    >
                      {link.text}
                    </Link>
                  ))}
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}
                >
                  {quickLinksCol2.map((link, index) => (
                    <Link
                      key={index}
                      to={link.href}
                      onClick={() => handleLinkClick(link.href, link.text)}
                      style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.color = 'var(--color-primary)')
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.color = 'rgba(255, 255, 255, 0.7)')
                      }
                    >
                      {link.text}
                    </Link>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Spacer for better alignment */}
            <Grid
              item
              xs={0}
              md={2}
              sx={{ display: { xs: 'none', md: 'block' } }}
            />

            {/* Contact Info Section */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--color-primary)',
                }}
              >
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon
                    sx={{
                      fontSize: '1.1rem',
                      color: 'var(--color-primary)',
                      flexShrink: 0,
                    }}
                  />
                  <SecureEmail
                    emailKey="collective"
                    component="span"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      textDecoration: 'underline',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'var(--color-primary)',
                      },
                      cursor: 'pointer',
                    }}
                    fallbackText="Contact email (JavaScript required)"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LinkedInIcon
                    sx={{
                      fontSize: '1.1rem',
                      color: 'var(--color-primary)',
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="a"
                    href="https://www.linkedin.com/company/rsm-collective/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackCTA('footer_social', 'LinkedIn', {
                        destination:
                          'https://www.linkedin.com/company/rsm-collective/',
                        section: 'contact_info',
                      });
                    }}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'var(--color-primary)',
                      },
                    }}
                  >
                    LinkedIn
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                >
                  <LocationOnIcon
                    sx={{
                      fontSize: '1.1rem',
                      color: 'var(--color-primary)',
                      flexShrink: 0,
                      mt: 0.3,
                    }}
                  />
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    401 West A St. Ste. 200
                    <br />
                    San Diego, CA 92101
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Legal Links Sub-Footer */}
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          py: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 1, sm: 3 },
              flexWrap: 'wrap',
            }}
          >
            {legalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                onClick={() => {
                  trackCTA('footer_legal', link.text, {
                    destination: link.href,
                    section: 'legal_links',
                  });
                }}
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  textDecoration: 'none',
                  fontSize: '0.8rem',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) =>
                  (e.target.style.color = 'var(--color-primary)')
                }
                onMouseLeave={(e) =>
                  (e.target.style.color = 'rgba(255, 255, 255, 0.5)')
                }
              >
                {link.text}
              </Link>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Bottom Bar */}
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          py: 2.5,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.875rem',
                }}
              >
                © {new Date().getFullYear()} RSM Collective, LLC dba Ready
                Secure Managed Cloud. All rights reserved.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontSize: '0.75rem',
                  mt: 0.5,
                }}
              >
                All trademarks are the property of their respective owners.
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem',
                textAlign: { xs: 'center', md: 'right' },
              }}
            >
              When People Thrive, Creativity Follows™
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Footer;
